import { useEffect, useState } from 'react';
import { FormField, TextInput, Toggle, SettingsSection } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { adminApi, type KeywordAlerts } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';

interface AgentOption { id: number; name: string }

const MAX_KEYWORDS = 100;

/**
 * "התראות מילות מפתח" — סקציה בתוך טאב "חברה" בהגדרות: הפעלה, מילות מפתח
 * ונציגים לניטור. מנוע התמלול ומפתח OpenRouter נקבעים ע"י הנהלת המערכת
 * פר-מרכזיה (מסך הלקוחות בסופר-אדמין) ואינם נערכים כאן.
 */
export function KeywordAlertsSection({ value, onChanged }: { value?: KeywordAlerts; onChanged: () => void }) {
  const [enabled, setEnabled] = useState(value?.enabled ?? false);
  const [keywords, setKeywords] = useState<string[]>(value?.keywords ?? []);
  const [agentIds, setAgentIds] = useState<number[]>(value?.agent_ids ?? []);
  const [draftKw, setDraftKw] = useState('');
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void adminApi.agents({ per_page: 200 })
      .then((r) => setAgents(((r.data ?? []) as AgentOption[]).map((a) => ({ id: a.id, name: a.name }))))
      .catch(() => {});
  }, []);

  function addKeyword() {
    const kw = draftKw.trim();
    if (!kw) return;
    if (keywords.includes(kw)) { setDraftKw(''); return; }
    if (keywords.length >= MAX_KEYWORDS) { toast(`עד ${MAX_KEYWORDS} מילות מפתח`, 'warn'); return; }
    setKeywords([...keywords, kw]);
    setDraftKw('');
  }

  async function save() {
    setSaving(true);
    try {
      await adminApi.updateKeywordAlerts({ enabled, keywords, agent_ids: agentIds });
      toast('הגדרות ההתראות נשמרו', 'ok');
      onChanged();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה בשמירת ההגדרות', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <SettingsSection
      title="התראות מילות מפתח"
      icon="bolt"
      iconColor="var(--warn)"
      selfSaving
      description="כשמילת מפתח נאמרת בשיחה, מנהל/מפקח יקבל התראה עם אפשרות להאזין. מנוע התמלול נקבע על-ידי הנהלת המערכת."
    >
      <div className="stack">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 12px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
          <div style={{ fontWeight: 600 }}>זיהוי מילות מפתח בשיחות</div>
          <Toggle checked={enabled} onChange={setEnabled} />
        </div>

        <FormField label={`מילות מפתח (${keywords.length}/${MAX_KEYWORDS})`} hint="הקש Enter כדי להוסיף מילה.">
          <div style={{ display: 'flex', gap: 8 }}>
            <TextInput
              value={draftKw}
              onChange={(e) => setDraftKw(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }}
              placeholder="לדוגמה: מבטל, מנהל, עורך דין"
            />
            <button type="button" className="btn" onClick={addKeyword}>הוסף</button>
          </div>
        </FormField>
        {keywords.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {keywords.map((kw) => (
              <span key={kw} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px 2px 10px', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-pill)', fontSize: 'var(--t-sm)' }}>
                {kw}
                <button type="button" aria-label={`הסר ${kw}`} className="btn ghost" style={{ height: 20, width: 20, padding: 0 }} onClick={() => setKeywords(keywords.filter((k) => k !== kw))}>
                  <Icon name="close" size={12} />
                </button>
              </span>
            ))}
          </div>
        )}

        <FormField label="נציגים לניטור" hint="ריק = כל הנציגים.">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {agents.length === 0 ? (
              <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>אין נציגים.</span>
            ) : agents.map((a) => {
              const checked = agentIds.includes(a.id);
              return (
                <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-sm)' }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => setAgentIds(e.target.checked ? [...agentIds, a.id] : agentIds.filter((x) => x !== a.id))}
                  />
                  {a.name}
                </label>
              );
            })}
          </div>
        </FormField>

        <div>
          <button type="button" className="btn primary" onClick={save} disabled={saving}>{saving ? 'שומר…' : 'שמור התראות'}</button>
        </div>
      </div>
    </SettingsSection>
  );
}
